import React, { useEffect, useRef, useState } from 'react';
import { IconMusic, IconPause } from './ui/Icons';

export default function AudioPlayer({ audio, accent = '#0f172a' }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const fadeIntervalRef = useRef(null);

  // Autoplay con fade-in (el usuario ya hizo click en "Abrir invitación")
  useEffect(() => {
    if (!audio?.url) return;
    const el = audioRef.current;
    if (!el) return;

    // Empieza en volumen 0 para el fade-in
    el.volume = 0;

    const TARGET_VOLUME = 0.5;
    const FADE_DURATION = 2000; // 2 segundos
    const STEPS = 40;
    const STEP_TIME = FADE_DURATION / STEPS;
    const STEP_VALUE = TARGET_VOLUME / STEPS;

    const startFadeIn = () => {
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);

      let currentVol = 0;
      fadeIntervalRef.current = setInterval(() => {
        currentVol += STEP_VALUE;
        if (currentVol >= TARGET_VOLUME) {
          currentVol = TARGET_VOLUME;
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
        }
        if (el) el.volume = currentVol;
      }, STEP_TIME);
    };

    const tryPlay = () => {
      el.play()
        .then(() => {
          setPlaying(true);
          startFadeIn();
        })
        .catch(() => {
          setPlaying(false);
        });
    };

    // Si el audio ya está listo, reproducir de inmediato
    if (el.readyState >= 2) {
      tryPlay();
    } else {
      el.addEventListener('canplay', tryPlay, { once: true });
    }

    return () => {
      el.removeEventListener('canplay', tryPlay);
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    };
  }, [audio?.url]);

  if (!audio?.url) return null;

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;

    if (playing) {
      el.pause();
      setPlaying(false);
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    } else {
      el.play()
        .then(() => {
          setPlaying(true);
          // Si el volumen quedó muy bajo, lo subimos directo sin fade
          if (el.volume < 0.5) el.volume = 0.5;
        })
        .catch(() => {});
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={audio.url}
        loop
        preload="auto"
      />

      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pausar música' : 'Reproducir música'}
        title={playing ? 'Pausar música' : 'Reproducir música'}
        className="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full shadow-lg border border-slate-200 bg-white grid place-items-center hover:shadow-xl transition-all active:scale-95"
        style={{ color: accent }}
      >
        <span className="block w-5 h-5">
          {playing ? (
            <IconPause className="w-full h-full" />
          ) : (
            <IconMusic className="w-full h-full" />
          )}
        </span>

        {/* Pulso animado cuando está reproduciendo */}
        {playing && (
          <span
            className="absolute inset-0 rounded-full animate-ping opacity-20 pointer-events-none"
            style={{ background: accent }}
          />
        )}
      </button>
    </>
  );
}